import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grow,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
  ChangeEvent,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DynamicIcon } from 'src/components';
import { useSubscriptionPause } from 'src/graphql/entities/subscription-pause';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { getFormattedDate, getHumanReadableDate } from 'src/utils/date';
import { useArrowNavigation, useNotifications } from 'src/utils/hooks';
import { getDateFromNaturalLanguage } from 'src/utils/natural-language-date';

interface PauseItem {
  text: string;
  date?: () => string;
  onClick?: () => void;
}

const ModalTransition = forwardRef(function ModalTransition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Grow ref={ref} {...props} />;
});

const INITIAL_LIST_ITEMS: PauseItem[] = [
  {
    text: 'One week',
    date: () => dayjs().add(1, 'week').endOf('day').toISOString(),
  },
  {
    text: 'Two weeks',
    date: () => dayjs().add(2, 'weeks').endOf('day').toISOString(),
  },
  {
    text: 'One month',
    date: () => dayjs().add(1, 'month').endOf('day').toISOString(),
  },
  {
    text: 'Three months',
    date: () => dayjs().add(3, 'months').endOf('day').toISOString(),
  },
];

export const PauseSubscriptionModal = () => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const { modalProps } = useModalState(Modals.PauseSubscription);
  const { closeModal } = useModalMethods(Modals.PauseSubscription);
  const { isOpened } = useModalState(Modals.PauseSubscription);
  const { pauseSubscription } = useSubscriptionPause();

  const { showErrorAlert } = useNotifications();

  const initialListItems = useMemo(() => {
    const listItems = [...INITIAL_LIST_ITEMS];

    listItems.push({
      text: 'Custom',
      date: () => '',
      onClick: () => {
        setPickerOpen(true);
      },
    });

    return listItems;
  }, []);

  const [listItems, setListItems] = useState(initialListItems);
  const [input, setInput] = useState('');
  const [customDate, setCustomDate] = useState<dayjs.Dayjs | null>(dayjs().add(1, 'week'));

  const closePauseModal = useCallback(() => {
    closeModal();
    setInput('');
  }, [closeModal]);

  useEffect(() => {
    if (isOpened) {
      setListItems(initialListItems);
    }
  }, [initialListItems, isOpened]);

  const submitPause = useCallback(
    (date?: string) => {
      if (!date || !modalProps?.address?.addressId || !modalProps?.address?.partnerId) {
        return;
      }

      const diff = new Date(date).getTime() - new Date().getTime();
      if (diff <= 0) {
        showErrorAlert('The pause date must be in the future');
        return;
      }

      closePauseModal();
      void pauseSubscription(modalProps.address.addressId, modalProps.address.partnerId, date);
    },
    [closePauseModal, showErrorAlert, pauseSubscription, modalProps],
  );

  const handleOnItemClick = useCallback(
    (item: PauseItem) => {
      const date = item.date?.();
      submitPause(date);
    },
    [submitPause],
  );

  const handleOnChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { value } = target;
      setInput(value);
      const parsedDate = getDateFromNaturalLanguage(value, {
        dayStart: '11:59pm',
      });

      if (!value) {
        setListItems(initialListItems);
      } else if (!parsedDate) {
        setListItems([]);
      } else {
        setListItems([
          {
            text: '',
            date: () => dayjs(parsedDate).endOf('day').toISOString(),
          },
        ]);
      }
    },
    [initialListItems],
  );

  const handleOnDatePickerAccept = useCallback(
    (value: dayjs.Dayjs | null) => {
      setPickerOpen(false);
      submitPause(value?.endOf('day').toISOString());
      setCustomDate(dayjs().add(1, 'week'));
    },
    [submitPause],
  );

  const onHandleKeyDown = useArrowNavigation();

  const dialogRef = useRef<HTMLDivElement | null>(null);

  return isOpened ? (
    <Dialog
      open={isOpened}
      onClose={closePauseModal}
      fullWidth
      TransitionComponent={ModalTransition}
    >
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight="bold">
            Pause Subscription
          </Typography>
          <Typography variant="overline">Pause</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent ref={dialogRef} onKeyDown={(e) => onHandleKeyDown(e, dialogRef)}>
        <TextField
          autoFocus
          placeholder="Try 2 weeks, next month, Aug 16th"
          type="text"
          fullWidth
          variant="standard"
          value={input}
          onChange={handleOnChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DynamicIcon
                  name="pause_circle"
                  sx={{
                    width: '1.5rem',
                    height: '1.5rem',
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
        <List>
          {listItems.map((item) => (
            <ListItemButton
              key={item.date?.() || item.text}
              onClick={() => item.onClick?.() ?? handleOnItemClick(item)}
            >
              <ListItemText
                primary={
                  item.text || getHumanReadableDate(item.date?.(), false)
                }
                sx={{
                  width: 'min-content',
                  '&::first-letter': {
                    textTransform: 'capitalize',
                  },
                }}
              />
              <ListItemText
                secondary={getFormattedDate(item.date?.())}
                sx={{
                  textAlign: 'end',
                  width: 'min-content',
                }}
              />
            </ListItemButton>
          ))}
          <DatePicker
            label="Custom date"
            disablePast
            minDate={dayjs().add(1, 'day')}
            open={pickerOpen}
            value={customDate}
            onChange={(newValue) => setCustomDate(newValue)}
            onAccept={(value) => handleOnDatePickerAccept(value)}
            onOpen={() => setPickerOpen(true)}
            onClose={() => {
              setPickerOpen(false);
              setCustomDate(dayjs().add(1, 'week'));
            }}
            aria-hidden="true"
            sx={{
              visibility: 'hidden',
              position: 'absolute',
              opacity: '0',
              bottom: '0',
              zIndex: '-1',
            }}
          />
        </List>
      </DialogContent>
    </Dialog>
  ) : null;
};

export default memo(PauseSubscriptionModal);
